import React, { useEffect, useMemo, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import { AiOutlineSearch } from 'react-icons/ai';
import { BsExclamationTriangle, BsTrash } from 'react-icons/bs';
import { IoCloseCircle } from 'react-icons/io5';
import {
    NAME_MAX_LENGTH,
    ROUTE_AUTH_STATUS,
    createRoute,
    formatMoney,
    salespersonLabel,
    summarizeCustomerGoals,
    updateRoute,
    validateRoute,
} from '../../services/salesRoutesService';
import styles from '../../css/SalesRoutes.module.css';

// Se pintan pocas filas a la vez: la búsqueda filtra todo el catálogo, pero el DOM queda
// acotado aunque la compañía tenga miles de clientes.
const PICKER_PAGE_SIZE = 25;
const SELECTED_STEP = 50;
const SEARCH_DEBOUNCE_MS = 200;
const SELECTED_FILTER_MIN = 10;

const toTokens = (text) => text.trim().toLowerCase().split(/\s+/).filter(Boolean);
const matchesTokens = (text, tokens) => tokens.every((t) => text.includes(t));
const place = (c) => [c.city, c.state].filter(Boolean).join(', ');
const snapshotOf = (name, salespersonNo, selected) => JSON.stringify([name.trim(), salespersonNo, selected.map((c) => c.customerNo)]);

// Conserva en la lista un vendedor que ya no venga en el catálogo, para no perderlo al editar.
export const salespersonOptions = (salespeople, currentNo) => (
    !currentNo || salespeople.some((s) => s.salespersonNo === currentNo)
        ? salespeople
        : [...salespeople, { salespersonNo: currentNo, name: currentNo }]
);

export default function RouteEditor({ idCompany, route, customers, salespeople, routes, goals, autoAuthorize, onCancel, onSaved }) {
    const [name, setName] = useState(route ? route.name : '');
    const [salespersonNo, setSalespersonNo] = useState(route ? route.salespersonNo : '');
    const [selected, setSelected] = useState(route ? route.customers : []);
    const [attempted, setAttempted] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    const [tab, setTab] = useState(route ? 'selected' : 'add');

    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [selectedFilter, setSelectedFilter] = useState('');
    const [selectedLimit, setSelectedLimit] = useState(SELECTED_STEP);

    const nameRef = useRef(null);
    const salespersonRef = useRef(null);
    const customersRef = useRef(null);
    const initialSnapshot = useRef(snapshotOf(name, salespersonNo, selected));

    useEffect(() => { window.scrollTo(0, 0); }, []);

    useEffect(() => {
        const timer = setTimeout(() => { setSearch(searchInput); setPage(1); }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const form = { name, salespersonNo, customers: selected };
    const errors = attempted ? validateRoute(form) : {};

    const selectedNos = useMemo(() => new Set(selected.map((c) => c.customerNo)), [selected]);
    const selectedGoals = useMemo(() => summarizeCustomerGoals(selected, goals), [selected, goals]);

    // Cliente → otras rutas donde ya está. No se bloquea: solo se avisa.
    const otherRoutesByCustomer = useMemo(() => {
        const index = new Map();
        routes.forEach((r) => {
            if (route && r.id === route.id) return;
            r.customers.forEach((c) => {
                if (!index.has(c.customerNo)) index.set(c.customerNo, []);
                index.get(c.customerNo).push(r.name);
            });
        });
        return index;
    }, [routes, route]);

    const matches = useMemo(() => {
        const tokens = toTokens(search);
        return tokens.length === 0 ? customers : customers.filter((c) => matchesTokens(c.searchText, tokens));
    }, [customers, search]);

    const pageCount = Math.max(1, Math.ceil(matches.length / PICKER_PAGE_SIZE));
    const currentPage = Math.min(page, pageCount);
    const pageStart = (currentPage - 1) * PICKER_PAGE_SIZE;
    const pageRows = matches.slice(pageStart, pageStart + PICKER_PAGE_SIZE);

    const selectedMatches = useMemo(() => {
        const tokens = toTokens(selectedFilter);
        if (tokens.length === 0) return selected;
        return selected.filter((c) => matchesTokens(`${c.customerNo} ${c.name} ${c.city} ${c.state}`.toLowerCase(), tokens));
    }, [selected, selectedFilter]);

    const toggleCustomer = (customer) => {
        setSelected((current) => (
            current.some((c) => c.customerNo === customer.customerNo)
                ? current.filter((c) => c.customerNo !== customer.customerNo)
                : [...current, { customerNo: customer.customerNo, name: customer.name, city: customer.city, state: customer.state }]
        ));
    };

    const removeCustomer = (customerNo) => {
        setSelected((current) => current.filter((c) => c.customerNo !== customerNo));
    };

    const focusFirstError = (fieldErrors) => {
        if (fieldErrors.name) nameRef.current.focus();
        else if (fieldErrors.salespersonNo) salespersonRef.current.focus();
        else if (fieldErrors.customers) {
            setTab('add');
            customersRef.current.focus();
        }
    };

    const save = async () => {
        setAttempted(true);
        setSaveError('');
        const fieldErrors = validateRoute(form);
        if (Object.keys(fieldErrors).length > 0) {
            focusFirstError(fieldErrors);
            return;
        }
        // Guardar sin cambios no debe mandar una ruta autorizada de vuelta a revisión.
        if (route && snapshotOf(name, salespersonNo, selected) === initialSnapshot.current) {
            onCancel();
            return;
        }
        setSaving(true);
        try {
            const options = { autoAuthorize };
            const saved = route ? await updateRoute(idCompany, route.id, form, options) : await createRoute(idCompany, form, options);
            onSaved(saved, !route);
        } catch (e) {
            setSaveError(e.fieldErrors ? Object.values(e.fieldErrors).join(' ') : e.message);
            setSaving(false);
        }
    };

    const cancel = () => {
        if (snapshotOf(name, salespersonNo, selected) === initialSnapshot.current) {
            onCancel();
            return;
        }
        Swal.fire({
            icon: 'warning',
            title: 'Discard your changes?',
            showCancelButton: true,
            confirmButtonText: 'Discard',
            cancelButtonText: 'Keep editing',
        }).then((result) => { if (result.isConfirmed) onCancel(); });
    };

    const options = salespersonOptions(salespeople, salespersonNo);
    const isRejected = route && route.authStatus === ROUTE_AUTH_STATUS.rejected;

    return (
        <div className={styles.editor}>
            {isRejected && (
                <div className={styles.rejectionNotice}>
                    <strong>This route was rejected.</strong>{' '}
                    {route.rejectionComment ? <span className={styles.preWrap}>{route.rejectionComment}</span> : 'No comment was left.'}
                </div>
            )}
            <div className={styles.authNotice} role="note">
                {autoAuthorize
                    ? 'This route will be authorized when you save it.'
                    : 'When you save, this route goes through an authorization process and its status changes to Pending to review until a super admin authorizes or rejects it.'}
            </div>

            <section className={styles.panel} aria-labelledby="sr-details-title">
                <h2 id="sr-details-title" className={styles.panelTitle}>Route details</h2>
                <div className={styles.fieldRow}>
                    <div className={styles.field}>
                        <label htmlFor="sr-name" className={styles.label}>Route name</label>
                        <input
                            id="sr-name"
                            ref={nameRef}
                            className={`${styles.input} ${errors.name ? styles.inputInvalid : ''}`}
                            value={name}
                            maxLength={NAME_MAX_LENGTH}
                            onChange={(e) => setName(e.target.value)}
                            aria-invalid={Boolean(errors.name)}
                            aria-describedby={errors.name ? 'sr-name-error' : undefined}
                        />
                        {errors.name && <p id="sr-name-error" className={styles.fieldError}>{errors.name}</p>}
                    </div>
                    <div className={styles.field}>
                        <label htmlFor="sr-salesperson" className={styles.label}>Salesperson</label>
                        <select
                            id="sr-salesperson"
                            ref={salespersonRef}
                            className={`${styles.input} ${errors.salespersonNo ? styles.inputInvalid : ''}`}
                            value={salespersonNo}
                            onChange={(e) => setSalespersonNo(e.target.value)}
                            aria-invalid={Boolean(errors.salespersonNo)}
                            aria-describedby={errors.salespersonNo ? 'sr-salesperson-error' : undefined}
                        >
                            <option value="">Select a salesperson</option>
                            {options.map((s) => <option key={s.salespersonNo} value={s.salespersonNo}>{salespersonLabel(s)}</option>)}
                        </select>
                        {errors.salespersonNo && <p id="sr-salesperson-error" className={styles.fieldError}>{errors.salespersonNo}</p>}
                    </div>
                </div>
            </section>

            <section aria-labelledby="sr-customers-title">
                <div ref={customersRef} tabIndex={-1} className={styles.customersHead}>
                    <h2 id="sr-customers-title" className={styles.panelTitle}>Customers</h2>
                    {errors.customers && <p className={styles.fieldError}>{errors.customers}</p>}
                </div>

                <div className={`${styles.tabs} ${styles.tabsMobileOnly}`} role="tablist" aria-label="Route customers">
                    <button type="button" role="tab" aria-selected={tab === 'add'} className={`${styles.tab} ${tab === 'add' ? styles.tabActive : ''}`} onClick={() => setTab('add')}>
                        Add customers
                    </button>
                    <button type="button" role="tab" aria-selected={tab === 'selected'} className={`${styles.tab} ${tab === 'selected' ? styles.tabActive : ''}`} onClick={() => setTab('selected')}>
                        In this route ({selected.length})
                    </button>
                </div>

                <div className={styles.customerPanels}>
                    {/* Buscador de todo el catálogo */}
                    <div className={`${styles.panel} ${tab === 'add' ? '' : styles.panelInactive}`}>
                        <h3 className={styles.panelSubtitle}>Add customers</h3>
                        <div className={styles.searchBox}>
                            <AiOutlineSearch className={styles.searchIcon} aria-hidden="true" />
                            <input
                                className={styles.searchInput}
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                placeholder="Name, number, address, city, phone…"
                                aria-label="Search customers"
                            />
                            {searchInput && (
                                <button type="button" className={styles.searchClear} onClick={() => setSearchInput('')} aria-label="Clear search">
                                    <IoCloseCircle aria-hidden="true" />
                                </button>
                            )}
                        </div>
                        <p className={styles.resultCount} aria-live="polite">
                            {matches.length === 0
                                ? 'No customers match your search.'
                                : `Showing ${pageStart + 1}–${pageStart + pageRows.length} of ${matches.length.toLocaleString()}`}
                        </p>
                        <ul className={styles.rows}>
                            {pageRows.map((c) => {
                                const others = otherRoutesByCustomer.get(c.customerNo);
                                const checked = selectedNos.has(c.customerNo);
                                return (
                                    <li key={c.customerNo}>
                                        <label className={`${styles.pickRow} ${checked ? styles.pickRowChecked : ''}`}>
                                            <input type="checkbox" className={styles.pickCheck} checked={checked} onChange={() => toggleCustomer(c)} />
                                            <span className={styles.rowText}>
                                                <span className={styles.rowName}>{c.name}</span>
                                                <span className={styles.rowMeta}>{[c.customerNo, c.address, place(c)].filter(Boolean).join(' · ')}</span>
                                                {goals.get(c.customerNo) && <span className={styles.goalChip}>Goal {formatMoney(goals.get(c.customerNo))}</span>}
                                                {others && <span className={styles.rowNotice}><BsExclamationTriangle aria-hidden="true" /> Also in: {others.join(', ')}</span>}
                                            </span>
                                        </label>
                                    </li>
                                );
                            })}
                        </ul>
                        {pageCount > 1 && (
                            <div className={styles.pager}>
                                <button type="button" className={styles.btnSecondary} disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}>Previous</button>
                                <span className={styles.pagerLabel}>Page {currentPage} of {pageCount.toLocaleString()}</span>
                                <button type="button" className={styles.btnSecondary} disabled={currentPage === pageCount} onClick={() => setPage(currentPage + 1)}>Next</button>
                            </div>
                        )}
                    </div>

                    {/* Clientes ya en la ruta */}
                    <div className={`${styles.panel} ${tab === 'selected' ? '' : styles.panelInactive}`}>
                        <h3 className={styles.panelSubtitle}>In this route ({selected.length})</h3>
                        {selected.length > SELECTED_FILTER_MIN && (
                            <div className={styles.searchBox}>
                                <AiOutlineSearch className={styles.searchIcon} aria-hidden="true" />
                                <input
                                    className={styles.searchInput}
                                    value={selectedFilter}
                                    onChange={(e) => { setSelectedFilter(e.target.value); setSelectedLimit(SELECTED_STEP); }}
                                    placeholder="Filter this route's customers…"
                                    aria-label="Filter this route's customers"
                                />
                            </div>
                        )}
                        {selected.length === 0 && <p className={styles.emptyInline}>No customers yet. Add them from the search.</p>}
                        {selected.length > 0 && selectedMatches.length === 0 && <p className={styles.emptyInline}>No customers match this filter.</p>}
                        <ul className={styles.rows}>
                            {selectedMatches.slice(0, selectedLimit).map((c) => (
                                <li key={c.customerNo} className={styles.selectedRow}>
                                    <span className={styles.rowText}>
                                        <span className={styles.rowName}>{c.name}</span>
                                        <span className={styles.rowMeta}>{[c.customerNo, place(c)].filter(Boolean).join(' · ')}</span>
                                        {goals.get(c.customerNo) && <span className={styles.goalChip}>Goal {formatMoney(goals.get(c.customerNo))}</span>}
                                    </span>
                                    <button type="button" className={styles.iconBtn} onClick={() => removeCustomer(c.customerNo)} aria-label={`Remove ${c.name}`}>
                                        <BsTrash aria-hidden="true" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                        {selectedMatches.length > selectedLimit && (
                            <button type="button" className={`${styles.btnSecondary} ${styles.showMore}`} onClick={() => setSelectedLimit(selectedLimit + SELECTED_STEP)}>
                                Show more ({selectedMatches.length - selectedLimit} left)
                            </button>
                        )}
                    </div>
                </div>
            </section>

            <div className={styles.saveBar}>
                {saveError && <p className={styles.saveError} role="alert">{saveError}</p>}
                <div className={styles.saveBarActions}>
                    <span className={styles.saveBarCount}>
                        {selected.length} {selected.length === 1 ? 'customer' : 'customers'}
                        {selectedGoals.withGoal > 0 && <span className={styles.saveBarExpected}>{formatMoney(selectedGoals.expected)} expected</span>}
                    </span>
                    <button type="button" className={styles.btnSecondary} onClick={cancel} disabled={saving}>Cancel</button>
                    <button type="button" className={styles.btnPrimary} onClick={save} disabled={saving}>
                        {saving ? 'Saving…' : route ? 'Save changes' : 'Create route'}
                    </button>
                </div>
            </div>
        </div>
    );
}
